import {useState, useCallback, useEffect} from "react";
import redAPI from "../../../../network/redAPI";
import API from "../../../../network/dashboardAPI";
import {debounce} from "lodash";
import useAlert from '../../../../services/useAlert';

export default function useRelatedProducts(props, initialValues, itemId, mode = "create") {
	// State
	const alert = useAlert();
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [fetching, setFetching] = useState(false);
	const [products, setProducts] = useState([]);
	const [productTotal, setProductTotal] = useState(0);
	const [selectedProducts, setSelectedProducts] = useState([]);
	const [isView, setIsView] = useState(false);
	const [dialogImgOpen, setDialogImgOpen] = useState(false);
	const [selectedItem, setSelectedItem] = useState({});

	async function fetchRelatedProducts() {
		if (!itemId) return;
		setIsView(true)
		setFetching(true)
		try {
			const query = {product_id: itemId, limit: 10, page: 1};
			const res = await redAPI.GetRelatedProduct(query); // wait for fetch
			const related = await res.json();
			// wait for JSON parsing
			setSelectedProducts(related.data.list || []);               // safely set data
			setFetching(false)
		} catch (err) {
			console.error("Failed to fetch related products:", err);
			setSelectedProducts([]);
			setFetching(false)
		}
	}

	useEffect(() => {
		fetchRelatedProducts();
	}, [itemId]);

	// Fetch products
	const searchStoreProduct = useCallback(
		(searchVal = "", p = page, limit = rowsPerPage) => {
			setFetching(true);
			API.searchProductNew({
				search: searchVal,
				page: p + 1,
				isDeleted: 0,
				limit: limit,
			})
				.then((res) => res.json())
				.then((res) => {
					if (res.success) {
						setProducts(res.data.list);
						setProductTotal(res.data.total_item);
					}
					setFetching(false);
				})
				.catch((err) => {
					console.error(err);
					setFetching(false);
				});
		},
		[page, rowsPerPage]
	);

	// Debounced search input
	const handleSearchProduct = useCallback(
		debounce((e) => {
			const {value} = e.target;
			setIsView(false)
			setSearch(value);
			searchStoreProduct(value);
		}, 750),
		[searchStoreProduct]
	);

	// Pagination handlers
	const handleChangePage = useCallback(
		(event, newPage) => {
			setPage(newPage);
			searchStoreProduct(search, newPage, rowsPerPage);
		},
		[search, rowsPerPage, searchStoreProduct]
	);

	const handleChangeRowPerPage = useCallback(
		(event) => {
			const newLimit = parseInt(event.target.value, 10);
			setRowsPerPage(newLimit);
			setPage(0);
			searchStoreProduct(search, 0, newLimit);
		},
		[search, searchStoreProduct, mode]
	);

	// Product selection
	const isAdded = useCallback(
		(itemId) => selectedProducts.some((item) => item.item_id === itemId),
		[selectedProducts]
	);

	const handleSelectProducts = useCallback(
		async (item) => {
			try {
				const isCurrentlyAdded = isAdded(item.item_id);

				// Handle API calls for edit mode first
				if (mode !== "create") {
					const body = {
						product_id: itemId,
						related_id: item.item_id
					};

					if (isCurrentlyAdded) {
						await props.removeRelatedProduct(body, () => {
						});
					} else {
						await props.addRelatedProduct(body, () => {
						});
					}

					// Only update UI after successful API call
					setSelectedProducts((prev) => {
						if (isCurrentlyAdded) {
							if (prev.length === 1 && isView) setIsView(false);
							return prev.filter((p) => p.item_id !== item.item_id);
						} else {
							return [item, ...prev];
						}
					});

					// Show success feedback
					alert.toast(
						`Related product ${isCurrentlyAdded ? 'removed' : 'added'}`,
						'success'
					);

					// Refresh the list
					fetchRelatedProducts();
				} else {
					// For create mode, just update UI
					setSelectedProducts((prev) => {
						if (isCurrentlyAdded) {
							if (prev.length === 1 && isView) setIsView(false);
							return prev.filter((p) => p.item_id !== item.item_id);
						} else {
							return [item, ...prev];
						}
					});
				}

			} catch (error) {
				console.error('Error handling related product:', error);

				alert.error(
					'Operation Failed',
					`Failed to ${isAdded(item.item_id) ? 'remove' : 'add'} related product. Please try again.`
				);
			}
		},
		[isAdded, isView, mode, itemId, fetchRelatedProducts]
	);

	const handleRemoveRelatedProduct = async (item) => {
		if (props.g_loading) return;

		try {
			const body = {
				product_id: itemId,
				related_id: item.item_id
			};

			await props.removeRelatedProduct(body, () => {
			});

			// Update UI after successful removal
			setSelectedProducts(prev => prev.filter(p => p.item_id !== item.item_id));

			if (selectedProducts.length === 1 && isView) {
				setIsView(false);
			}

			alert.toast(
				`${item.name} removed from related products`,
				'success'
			);

			fetchRelatedProducts();

		} catch (error) {
			console.error('Error removing related product:', error);

			alert.error(
				'Remove Failed',
				`Failed to remove ${item.name}. Please try again.`
			);
		}
	};

	// Dialog handlers
	const handleOpenDialogImgOpen = useCallback((item = {}) => {
		setDialogImgOpen((prev) => !prev);
		setSelectedItem(item);
	}, []);

	const handleRemoveAllSelectedProduct = useCallback(async () => {
		const result = await alert.confirm(
			"Are you sure?",
			"Do you really want to deselect all selected item(s)?",
			"Yes, deselect",
			"Cancel"
		);

		if (!result.isConfirmed) return;

		setSelectedProducts([]);
		if (isView) setIsView(false);

		await alert.success("Deselected!", "All selected items have been removed.");
	}, [isView]);


	const handleToggleViewProducts = useCallback(() => {
		setIsView((prev) => !prev);
	}, []);

	// Return state and handlers
	return {
		search,
		page,
		rowsPerPage,
		fetching,
		products,
		productTotal,
		selectedProducts,
		isView,
		dialogImgOpen,
		selectedItem,
		searchStoreProduct,
		handleSearchProduct,
		handleChangePage,
		handleChangeRowPerPage,
		isAdded,
		handleSelectProducts,
		handleOpenDialogImgOpen,
		handleRemoveAllSelectedProduct,
		handleToggleViewProducts,
		handleRemoveRelatedProduct,
		setSearch,
	};
}
